import { Pill as MantinePill } from "@mantine/core";
import { useDebouncedValue, useDisclosure, useHover, useMergedRef } from "@mantine/hooks";
import { componentWrapper } from "../../utilities";
import styles from "./pill.module.scss";
import type { CustomPillFactory, IPillMenuProps, IProps, ISkeletonProps } from "./pill.types";
import { Group } from "../group";
import { Text } from "../text";
import { Icon } from "../icon";
import { AutoMenu } from "../auto-menu";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons";
import { ActionIcon } from "../action-icon";
import { useEffect } from "react";
import { Skeleton } from "../skeleton";

/**
 * Pill component for displaying tags or labels with optional icons.
 * @param {React.ReactNode} props.children - The content to display in the pill.
 * @param {IconDefinition} props.leftIcon - Optional icon to display on the left side.
 * @param {IconDefinition} props.rightIcon - Optional icon to display on the right side.
 * @param {string} props.size - The size of the pill.
 * @param {React.ReactNode} props.rightSection - Optional content to display on the right side.
 * @param {boolean} props.noEllipsis - When true, show the label in full and let the pill overflow.
 * @returns {JSX.Element} The Pill component.
 */
const Pill = componentWrapper<CustomPillFactory, IProps>(
    "Pill",
    ({ children, leftIcon, rightIcon, size = "sm", rightSection, noEllipsis = false, ...otherProps }, ref) => {
        return (
            <MantinePill
                ref={ref}
                classNames={{ root: styles["root"], label: styles["label"] }}
                size={size}
                data-clickable={typeof otherProps.onClick !== "undefined"}
                data-no-ellipsis={noEllipsis}
                {...otherProps}
            >
                <Group wrap="nowrap" h="100%" align="center" gap="xs">
                    <Group w="100%" wrap="nowrap" h="100%" align="center" gap="xs">
                        {leftIcon && <Icon className={styles["icon"]} icon={leftIcon} />}
                        <Text className={styles["text"]} variant="medium">
                            {children}
                        </Text>
                        {rightIcon && <Icon className={styles["icon"]} icon={rightIcon} />}
                    </Group>
                    {rightSection}
                </Group>
            </MantinePill>
        );
    }
);
/**
 * Pill component with an integrated menu that appears on hover.
 * @param {React.ReactNode} props.children - The content to display in the pill.
 * @param {IconDefinition} props.leftIcon - Optional icon to display on the left side.
 * @param {IconDefinition} props.rightIcon - Optional icon to display on the right side that changes to menu icon on hover.
 * @param {Array} props.menu - Array of menu items to display in the dropdown.
 * @returns {JSX.Element} The PillMenu component.
 */
const PillMenu = componentWrapper<CustomPillFactory, IPillMenuProps>(
    "PillMenu",
    ({ children, leftIcon, rightIcon, menu, ...otherProps }, ref) => {
        const { hovered: isPillHovered, ref: hoverRef } = useHover();
        const { hovered: isMenuHovered, ref: menuHoverRef } = useHover();
        // This debounced value is used to the menu don't get closed
        // when moving the mouse from the pill to the menu
        const [isPillHoveredDebounced] = useDebouncedValue(isPillHovered, 100);
        const mergedRef = useMergedRef(ref, hoverRef);
        const [isMenuOpen, menuHandlers] = useDisclosure(false);

        useEffect(() => {
            if (!isPillHoveredDebounced && !isMenuHovered) {
                menuHandlers.close();
            }
        }, [isPillHoveredDebounced, isMenuHovered, menuHandlers]);

        return (
            <AutoMenu opened={isMenuOpen} ref={menuHoverRef} items={menu ?? []} disabled={!menu?.length}>
                <Pill ref={mergedRef} {...otherProps}>
                    <Group wrap="nowrap" h="100%" gap="xs">
                        {leftIcon && <Icon className={styles["icon"]} icon={leftIcon} />}
                        <Text variant="medium">{children}</Text>
                        <ActionIcon
                            p="0"
                            variant="subtle"
                            color="black"
                            size="xxs"
                            bdrs={"xl"}
                            onClick={menuHandlers.toggle}
                        >
                            {isMenuHovered ||
                            !rightIcon ||
                            (rightIcon && ((isPillHovered && !isPillHoveredDebounced) || isPillHoveredDebounced)) ? (
                                <Icon className={styles["icon"]} icon={faEllipsis} />
                            ) : (
                                <Icon className={styles["icon"]} icon={rightIcon} />
                            )}
                        </ActionIcon>
                    </Group>
                </Pill>
            </AutoMenu>
        );
    }
);

/**
 * Skeleton loader component for Pill.
 * @returns {JSX.Element} The PillSkeleton component.
 */
const PillSkeleton = (props: ISkeletonProps) => {
    return <Skeleton className={styles["skeleton"]} {...props} />;
};

Pill.Group = MantinePill.Group;
Pill.Menu = PillMenu;

export { Pill, PillMenu, PillSkeleton };
